import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import StatusBadge from "../components/StatusBadge";
import Attachments from "../components/Attachments";
import { toast } from "sonner";
import { Check, Undo2, X, ExternalLink, Eye } from "lucide-react";

function documentPath(a) {
    if (a.document_type === "quotation") return `/quotations/${a.document_id}`;
    if (a.document_type === "sales_order") return `/sales-orders/${a.document_id}`;
    return null;
}

const peso = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 });

export default function Approvals() {
    const { user } = useAuth();
    const showPrices = user?.price_visibility !== "none";  // hidden-price group sees no return amounts here either
    const [rows, setRows] = useState([]);
    const [comment, setComment] = useState("");
    const stepLabel = (step) => ({
        bm_oh: "BM / OH", bm: "BM / OH", treasury: "Treasury · verify payment",
        accounting_process: "OK to Process", accounting: "OK to Process",
        accounting_release: "OK to Release",
    })[step] || step || "";
    const approveLabel = (a) => ({
        accounting_process: "OK to Process", accounting: "OK to Process",
        accounting_release: "OK to Release", treasury: "Verify Paid",
    })[a.approval_step] || "Approve";
    const [review, setReview] = useState(null);     // the approval row under review
    const [ret, setRet] = useState(null);           // fetched return detail
    const [ir, setIr] = useState(null);             // fetched inquiry_request detail
    const [retrying, setRetrying] = useState(false);
    const [depositBusy, setDepositBusy] = useState(false);
    const [riw, setRiw] = useState({});
    const [riwBusy, setRiwBusy] = useState(false);
    const load = async () => setRows((await api.get("/approvals")).data);
    useEffect(() => { load(); }, []);

    const act = async (a, action) => {
        try {
            await api.post(`/approvals/${a.id}/act`, { action, comment });
            toast.success(`${action} done`);
            setComment("");
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const openReview = async (a) => {
        setReview(a); setRet(null); setIr(null); setRiw({});
        try {
            if (a.document_type === "return") {
                const { data } = await api.get(`/returns/${a.document_id}`);
                setRet(data);
            } else if (a.document_type === "inquiry_request") {
                const { data } = await api.get(`/inquiry-requests/${a.document_id}`);
                setIr(data);
            }
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const setDepositStatus = async (status) => {
        setDepositBusy(true);
        try {
            const { data } = await api.put(`/inquiry-requests/${review.document_id}/deposit-status`, { status });
            setIr(data);
            toast.success("Deposit status updated");
        } catch (e) { toast.error(formatApiError(e)); }
        setDepositBusy(false);
    };

    const saveRiw = async () => {
        setRiwBusy(true);
        try {
            const lines = Object.entries(riw).map(([line_id, v]) => ({ line_id, ...v }));
            const { data } = await api.post(`/returns/${review.document_id}/adjust`, { lines });
            setRet(data); setRiw({});
            toast.success("Receiving (RIW) saved.");
        } catch (e) { toast.error(formatApiError(e)); }
        setRiwBusy(false);
    };

    return (
        <div className="p-6 space-y-4" data-testid="approvals-page">
            <div><div className="sc-section-title">Workflow</div><h1 className="text-2xl font-semibold tracking-tight">Approval Center</h1></div>

            <div className="sc-card p-3 flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Comment for next action:</span>
                <input className="sc-input max-w-md" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Optional comment…" data-testid="approval-comment" />
            </div>

            <div className="sc-card">
                <table className="w-full sc-table">
                    <thead><tr><th>When</th><th>Type</th><th>Doc no</th><th>Client</th><th>Agent</th><th>Approver role</th><th>Status</th><th>Review</th><th className="text-right">Actions</th></tr></thead>
                    <tbody>
                        {rows.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-muted-foreground">No approval requests.</td></tr>}
                        {rows.map((a) => (
                            <tr key={a.id} data-testid={`approval-row-${a.id}`}>
                                <td className="text-muted-foreground">{new Date(a.created_at).toLocaleString("en-PH", { timeZone: "Asia/Manila" })}</td>
                                <td>{a.document_type}</td>
                                <td className="font-mono">{a.document_no}</td>
                                <td>{a.client_name || a.customer_name || "—"}</td>
                                <td className="text-muted-foreground">{a.agent_name || a.requester_email}</td>
                                <td>{a.approver_role}{a.approval_step ? <span className="text-muted-foreground"> · {stepLabel(a.approval_step)}</span> : null}</td>
                                <td><StatusBadge status={a.status} /></td>
                                <td>
                                    {a.document_type === "return" ? (
                                        <button
                                            type="button"
                                            onClick={() => openReview(a)}
                                            className="h-7 px-2 inline-flex items-center gap-1 rounded border border-border text-xs hover:bg-secondary"
                                            data-testid={`review-return-${a.id}`}
                                        >
                                            <Eye className="w-3 h-3" />
                                            Review
                                        </button>
                                    ) : a.document_type === "inquiry_request" ? (
                                        <button
                                            type="button"
                                            onClick={() => openReview(a)}
                                            className="h-7 px-2 inline-flex items-center gap-1 rounded border border-border text-xs hover:bg-secondary"
                                            data-testid={`review-inquiry-${a.id}`}
                                        >
                                            <Eye className="w-3 h-3" />
                                            Review
                                        </button>
                                    ) : documentPath(a) ? (
                                        <button
                                            type="button"
                                            onClick={() => window.location.assign(documentPath(a))}
                                            className="h-7 px-2 inline-flex items-center gap-1 rounded border border-border text-xs hover:bg-secondary"
                                            data-testid={`open-doc-${a.id}`}
                                        >
                                            <ExternalLink className="w-3 h-3" />
                                            Open
                                        </button>
                                    ) : (
                                        <span className="text-xs text-muted-foreground">—</span>
                                    )}
                                </td>
                                <td className="text-right">
                                    {a.status === "pending" ? (
                                        <div className="inline-flex items-center gap-1">
                                            <button onClick={() => act(a, "approve")} className="text-xs h-7 px-2 rounded bg-emerald-600 text-white" data-testid={`approve-${a.id}`}><Check className="w-3 h-3 inline" /> {approveLabel(a)}</button>
                                            <button onClick={() => act(a, "revise")} className="text-xs h-7 px-2 rounded border border-border" data-testid={`revise-${a.id}`}><Undo2 className="w-3 h-3 inline" /> Revise</button>
                                            <button onClick={() => act(a, "reject")} className="text-xs h-7 px-2 rounded bg-red-600 text-white" data-testid={`reject-${a.id}`}><X className="w-3 h-3 inline" /> Reject</button>
                                        </div>
                                    ) : (
                                        <span className="text-xs text-muted-foreground">{a.decided_by ? `by ${a.decided_by}` : "—"}</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {review && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4" onClick={() => setReview(null)}>
                    <div className="sc-card w-full max-w-2xl p-4 mt-10 space-y-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                            <div className="sc-section-title">
                                {review.document_type === "return" ? "Review return" : "Review Lead Time Inquiry"} · {review.document_no}
                            </div>
                            <button className="text-sm text-muted-foreground" onClick={() => setReview(null)}>Close</button>
                        </div>
                        {review.document_type === "inquiry_request" ? (
                            !ir ? (
                                <div className="text-sm text-muted-foreground py-6 text-center">Loading request…</div>
                            ) : (
                                <>
                                    <div className="grid grid-cols-2 gap-2 text-xs">
                                        <div><span className="text-muted-foreground">Item:</span> {ir.item_name || ir.item_code} {ir.size ? `· ${ir.size}` : ""}</div>
                                        <div><span className="text-muted-foreground">Qty:</span> {ir.quantity} {ir.bxs ? `(${ir.bxs} bx ${ir.pcs || 0} pc)` : ""}</div>
                                        <div><span className="text-muted-foreground">PO Cut-off:</span> {ir.po_cutoff_date || "—"}</div>
                                        <div><span className="text-muted-foreground">ETA:</span> {ir.eta || "—"}</div>
                                        <div><span className="text-muted-foreground">Down Payment:</span> {ir.down_payment || "—"}</div>
                                        <div className="col-span-2"><span className="text-muted-foreground">Remarks:</span> {ir.remarks || "—"}</div>
                                    </div>
                                    <div>
                                        <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Deposit slip (down-payment proof)</div>
                                        <Attachments collection="inquiry-requests" docId={review.document_id} editable={false}
                                            kinds={[["card_deposit_slip", "Deposit slip"], ["bdo_qr_ref", "BDO QR PH ref"], ["gcash_ref", "G-Cash ref"], ["other", "Other"]]}
                                            defaultKind="card_deposit_slip" />
                                    </div>
                                    {(user?.role === "treasury" || user?.role === "admin") && (
                                        <div className="rounded-md border border-border p-2 space-y-1.5">
                                            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Deposit status (Treasury assessment)</div>
                                            <div className="flex items-center gap-1.5">
                                                {[["cleared", "Cleared"], ["for_clearing", "For Clearing"], ["not_posted", "Not Posted"]].map(([k, label]) => (
                                                    <button key={k} type="button" disabled={depositBusy} onClick={() => setDepositStatus(k)}
                                                        className={`h-7 px-2 rounded border text-xs ${ir.deposit_status === k ? "bg-foreground text-background border-foreground" : "border-border hover:bg-secondary"}`}>
                                                        {label}
                                                    </button>
                                                ))}
                                            </div>
                                            {ir.deposit_status && <div className="text-[11px] text-muted-foreground">Set by {ir.deposit_status_by} · {ir.deposit_status_at ? new Date(ir.deposit_status_at).toLocaleString("en-PH", { timeZone: "Asia/Manila" }) : ""}</div>}
                                        </div>
                                    )}
                                </>
                            )
                        ) : !ret ? (
                            <div className="text-sm text-muted-foreground py-6 text-center">Loading return…</div>
                        ) : (
                            <>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                    <div><span className="text-muted-foreground">Customer:</span> {ret.customer_name || "—"}</div>
                                    <div><span className="text-muted-foreground">Type:</span> {({change_unpicked: "Change (unpicked)", change_picked_riw: "Change (RIW)", defective: "Defective"}[ret.return_type]) || ret.return_type}</div>
                                    <div><span className="text-muted-foreground">Source PO:</span> <span className="font-mono">{ret.source_po_no || ret.po_no || "—"}</span> {ret.source_order_doc_no ? <span className="text-muted-foreground">({ret.source_order_doc_no})</span> : null}</div>
                                    <div>
                                        <span className="text-muted-foreground">Replacement PO:</span> <span className="font-mono">{ret.replacement_po_no || "—"}</span>
                                        {ret.replacement_order ? (
                                            <span className="text-muted-foreground"> — {showPrices ? peso(ret.replacement_order.amount) + ", " : ""}{ret.replacement_order.date ? new Date(ret.replacement_order.date).toLocaleDateString("en-PH", { timeZone: "Asia/Manila" }) : "—"}</span>
                                        ) : null}
                                    </div>
                                    <div className="col-span-2"><span className="text-muted-foreground">Reason:</span> {ret.reason || "—"}</div>
                                    {ret.riw_ref ? <div><span className="text-muted-foreground">RIW ref:</span> {ret.riw_ref}</div> : null}
                                    {ret.pickup_status ? (
                                        <div className="col-span-2">
                                            <span className="text-muted-foreground">Pickup status (from SAP):</span>{" "}
                                            {ret.pickup_status.picked_up
                                                ? <span className="text-emerald-700">Picked up — Delivery {ret.pickup_status.deliveries[0]?.doc_num} ({ret.pickup_status.deliveries[0]?.doc_date})</span>
                                                : <span className="text-amber-700">Not yet picked up — no Delivery drawn from this order yet</span>}
                                        </div>
                                    ) : null}
                                </div>
                                {(() => {
                                    const canRiw = ret.status === "pending_approval" && ["inquiry_dept", "purchasing", "admin"].includes(user?.role);
                                    const setL = (id, k, v) => setRiw((pr) => ({ ...pr, [id]: { ...(pr[id] || {}), [k]: v } }));
                                    return (
                                        <div className="overflow-x-auto">
                                            {canRiw && <div className="text-[11px] text-muted-foreground mb-1">RIW review: record QC's <b>actual tonality</b> and <b>actual qty received</b>, or remove a line not received. The SAP credit memo posts against these.</div>}
                                            <table className="w-full sc-table">
                                                <thead><tr><th>Item</th><th>{canRiw ? "Tonality (actual)" : "Tonality"}</th><th className="text-right">{canRiw ? "Qty (actual)" : "Qty"}</th>{canRiw ? <th></th> : null}</tr></thead>
                                                <tbody>
                                                    {(ret.lines || []).map((l, i) => {
                                                        const declaredTon = (l.allocation?.allocated_batches || []).map((b) => b.tonality || b.batch_code).filter(Boolean).join(", ");
                                                        const e = riw[l.line_id] || {};
                                                        const removed = !!e.delete;
                                                        if (!canRiw) {
                                                            return <tr key={i}><td>{l.item_name || l.item_code}</td><td className="font-mono text-xs">{l.actual_tonality || declaredTon || "—"}</td><td className="text-right">{l.actual_quantity != null ? l.actual_quantity : l.quantity}</td></tr>;
                                                        }
                                                        return (
                                                            <tr key={i} className={removed ? "opacity-40 line-through" : ""}>
                                                                <td>{l.item_name || l.item_code}</td>
                                                                <td><input className="sc-input h-8 text-xs font-mono w-32" disabled={removed} placeholder={l.actual_tonality || declaredTon || "tonality"} value={e.actual_tonality != null ? e.actual_tonality : (l.actual_tonality || "")} onChange={(ev) => setL(l.line_id, "actual_tonality", ev.target.value)} data-testid="riw-tonality" /></td>
                                                                <td className="text-right"><input type="number" step="0.01" min="0" max={l.quantity} className="sc-input h-8 text-xs w-20 text-right" disabled={removed} placeholder={String(l.actual_quantity != null ? l.actual_quantity : l.quantity)} value={e.actual_quantity != null ? e.actual_quantity : (l.actual_quantity != null ? l.actual_quantity : "")} onChange={(ev) => setL(l.line_id, "actual_quantity", ev.target.value)} data-testid="riw-qty" /></td>
                                                                <td className="text-right"><button type="button" className="text-xs text-red-600 hover:underline" onClick={() => setL(l.line_id, "delete", !removed)}>{removed ? "Keep" : "Remove"}</button></td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                            {canRiw && (
                                                <div className="flex items-center justify-end mt-2">
                                                    <button type="button" disabled={riwBusy || !Object.keys(riw).length} onClick={saveRiw} className="h-8 px-3 rounded bg-slate-700 text-white text-xs font-medium disabled:opacity-50" data-testid="riw-save">Save receiving (RIW)</button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                                <div>
                                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1 mt-1">Received RIW (from QC)</div>
                                    <Attachments collection="returns" docId={review.document_id} editable={ret.status === "pending_approval" && ["inquiry_dept", "purchasing", "admin"].includes(user?.role)} kinds={[["received_riw", "Received RIW (QC)"], ["riw_form", "RIW form"], ["other", "Other"]]} defaultKind="received_riw" onlyKinds={["received_riw"]} />
                                </div>
                                <div className="text-right text-xs">
                                    {showPrices && <>Return subtotal: <b>{peso(ret.subtotal)}</b></>}
                                    {ret.riw_reviewed_by ? <span className="text-muted-foreground"> · RIW reviewed by {ret.riw_reviewed_by}</span> : null}
                                    {showPrices && ret.service_charge ? <span className="text-amber-700"> · Service charge ({Number(ret.service_charge_pct)}%): <b>{peso(ret.service_charge)}</b></span> : null}
                                </div>
                                {ret.status === "approved" && ret.sap_status !== "posted_to_sap" && (
                                    <div className="flex items-center justify-between rounded-md border border-amber-200 bg-amber-500/5 p-2 text-xs">
                                        <span>Not yet posted to SAP ({ret.sap_status || "not_posted"}).</span>
                                        <button
                                            className="rounded bg-amber-600 px-2 py-1 text-white disabled:opacity-50"
                                            disabled={retrying}
                                            onClick={async () => {
                                                setRetrying(true);
                                                try {
                                                    const { data } = await api.post(`/returns/${review.document_id}/retry-credit-memo`);
                                                    setRet(data);
                                                    toast.success(`Posted — A/R Credit Memo ${data.sap_credit_memo_docnum}`);
                                                } catch (e) { toast.error(formatApiError(e)); }
                                                setRetrying(false);
                                            }}
                                        >
                                            {retrying ? "Posting…" : "Retry SAP Posting"}
                                        </button>
                                    </div>
                                )}
                                <div>
                                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Item photos (for assessment)</div>
                                    <Attachments collection="returns" docId={review.document_id} editable={false} kinds={[["item_photo", "For assessment"], ["riw_form", "RIW form"], ["other", "Other"]]} defaultKind="item_photo" onlyKinds={["item_photo"]} />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
